import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppName } from '../../apps/definition';
import { PromptModalTypes } from '../../models/Prompts';
import {
  QAControllerEventArgs,
  useQAController,
} from '../../scripts/QAController';
import { Modal } from '../controls/ui/Modal/Modal';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { UIIconButton } from '../controls/ui/UIIconButton/UIIconButton';
import { CreatePromptTemplate } from '../promptModal/CreatePromptTemplate';
import { ViewPromptTemplate } from '../promptModal/ViewPromptTemplate';
import { DeletePromptTemplate } from './DeletePromptTemplate';
import { EditPromptTemplate } from './EditPromptTemplate';
import { PromptTemplateTable } from './PromptTemplateTable';

export interface IInnerComponent {
  onClose: () => void;
  promptId?: string;
}

interface IQAPromptTemplateModalProps {
  refetchPrompts?: () => Promise<void>;
}

export const QAPromptTemplateModal: React.FC<IQAPromptTemplateModalProps> = ({
  refetchPrompts,
}) => {
  const qaPageController = useQAController();
  const [heading, setHeading] = useState<string>('Workflows');
  const [showPromptModal, setShowPromptModal] = useState<boolean>(false);
  const [promptId, setPromptId] = useState<string | undefined>(undefined);
  const [promptModalType, setPromptModalType] = useState<string | undefined>(
    undefined
  );

  const [promptTemplate, setPromptTemplate] = useState<string | undefined>();
  const [searchFilters, setSearchFilters] = useState<AppName[]>([]);
  const [isAuthor, setIsAuthor] = useState<boolean>(false);

  const handleShowPromptModal = useCallback(
    ({
      isAuthor: incomingIsAuthor,
      promptModalType: incomingPromptModalType,
      promptId: incomingPromptId,
      promptTemplate: incomingPromptTemplate,
      searchFilters: incomingSearchFilters,
    }: Partial<QAControllerEventArgs>) => {
      setPromptModalType(incomingPromptModalType);
      if (incomingPromptId) {
        setPromptId(incomingPromptId);
      }

      if (incomingPromptTemplate) {
        setPromptTemplate(incomingPromptTemplate);
      }

      if (incomingSearchFilters) {
        setSearchFilters(incomingSearchFilters);
      }

      if (incomingIsAuthor) {
        setIsAuthor(incomingIsAuthor);
      } else {
        setIsAuthor(false);
      }

      setShowPromptModal(true);
    },
    []
  );

  const closePromptModal = useCallback(() => {
    if (refetchPrompts) {
      refetchPrompts();
    }

    setShowPromptModal(false);
    setPromptModalType(undefined);
    setPromptId(undefined);
  }, [refetchPrompts]);

  const handleEditButtonClick = useCallback(() => {
    setPromptModalType('EDIT');
  }, []);

  const handleDeleteButtonClick = useCallback(() => {
    setPromptModalType('DELETE');
  }, []);

  const handleDuplicateButtonClick = useCallback(() => {
    setPromptModalType('DUPLICATE');
  }, []);

  useEffect(() => {
    qaPageController.listenEvent('showPromptModal', handleShowPromptModal);
    return () => {
      qaPageController.off('showPromptModal', handleShowPromptModal);
    };
  }, [handleShowPromptModal, qaPageController]);

  const innerComponent = useMemo(() => {
    if (promptModalType === PromptModalTypes.CREATE) {
      setHeading('New Workflow');
      return (
        <CreatePromptTemplate
          onClose={closePromptModal}
          searchFilterStrings={searchFilters}
          template={promptTemplate}
        />
      );
    }

    if (promptModalType === PromptModalTypes.DELETE) {
      setHeading('Delete Workflow');
      return (
        <DeletePromptTemplate onClose={closePromptModal} promptId={promptId} />
      );
    }

    if (promptModalType === PromptModalTypes.DUPLICATE) {
      setHeading('Duplicate Workflow');
      return (
        <EditPromptTemplate
          duplicate
          onClose={closePromptModal}
          promptId={promptId}
        />
      );
    }

    if (promptModalType === PromptModalTypes.EDIT) {
      setHeading('Edit Workflow');
      return (
        <EditPromptTemplate
          duplicate={false}
          onClose={closePromptModal}
          promptId={promptId}
        />
      );
    }

    if (promptModalType === PromptModalTypes.VIEW_TEMPLATE) {
      setHeading('Workflow Preview');
      return (
        <ViewPromptTemplate onClose={closePromptModal} promptId={promptId} />
      );
    }

    if (promptModalType === PromptModalTypes.VIEW_ALL_TEMPLATES) {
      setHeading('Workflows');
      return <PromptTemplateTable onClose={closePromptModal} />;
    }

    return null;
  }, [
    closePromptModal,
    promptId,
    promptModalType,
    promptTemplate,
    searchFilters,
  ]);

  const showTopActionButtons = useMemo(() => {
    if (promptModalType === 'VIEW_TEMPLATE') {
      return true;
    }

    return false;
  }, [promptModalType]);

  if (!showPromptModal) {
    return null;
  }

  return (
    <Modal modalClasses="h-[672px]" onClose={closePromptModal}>
      <div className="flex justify-between mb-6">
        <div className="flex gap-2 items-center">
          <UIIcon name="bolt" size={32} />
          <h2 className="text-2xl m-0 font-bold">{heading}</h2>
        </div>
        {showTopActionButtons && (
          <div className="flex gap-1">
            <div className="flex items-center py-1 px-2 rounded bg-cloud-15 hover:bg-cloud-20">
              <UIIconButton
                name="copy"
                onClick={handleDuplicateButtonClick}
                size={16}
                tooltip="Duplicate workflow"
              />
            </div>
            {isAuthor && (
              <>
                <div className="flex items-center py-1 px-2 rounded bg-cloud-15 hover:bg-cloud-20">
                  <UIIconButton
                    name="trash-can"
                    onClick={handleDeleteButtonClick}
                    size={16}
                    tooltip="Delete workflow"
                  />
                </div>
                <div className="flex items-center py-1 px-2 rounded bg-cloud-15 hover:bg-cloud-20">
                  <UIIconButton
                    name="pencil"
                    onClick={handleEditButtonClick}
                    size={16}
                    tooltip="Edit workflow"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {innerComponent}
    </Modal>
  );
};
